/**
 * Store Adapter - Makes MemoryStore compatible with BaseStore interface
 *
 * Sprint 11d: Bridge between @ownyou/memory-store's MemoryStore and
 * @langchain/langgraph's BaseStore interface required by @ownyou/iab-classifier.
 *
 * Key differences:
 * - MemoryStore.get() returns T | null
 * - BaseStore.get() returns Item | null (with key, namespace, createdAt, updatedAt, value)
 *
 * This adapter wraps MemoryStore and provides the BaseStore interface
 * needed by the full IAB classifier workflow.
 */

import type { MemoryStore } from '@ownyou/memory-store'
import type { BaseStore } from '@langchain/langgraph/web'

// Item interface matching BaseStore requirements
interface Item {
  value: Record<string, unknown>
  key: string
  namespace: string[]
  createdAt: Date
  updatedAt: Date
}

interface SearchItem extends Item {
  score?: number
}

// Operation types for batch operations
type Operation =
  | { namespace: string[]; key: string }  // GetOperation
  | { namespace: string[]; key: string; value: Record<string, unknown> | null; index?: false | string[] }  // PutOperation
  | { namespacePrefix: string[]; filter?: Record<string, unknown>; limit?: number; offset?: number; query?: string }  // SearchOperation
  | { matchConditions?: unknown[]; limit?: number; offset?: number }  // ListNamespacesOperation

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type OperationResults<_Op extends Operation[]> = unknown[]

/**
 * Adapter that wraps MemoryStore to provide BaseStore interface
 *
 * The iab-classifier package requires BaseStore from @langchain/langgraph,
 * which has a different interface than our @ownyou/memory-store.
 *
 * Main difference: BaseStore.get() returns Item with metadata,
 * while MemoryStore.get() returns value directly.
 */
export class MemoryStoreAdapter {
  private store: MemoryStore
  private itemCache: Map<string, { createdAt: Date; updatedAt: Date }> = new Map()

  constructor(store: MemoryStore) {
    this.store = store
  }

  /**
   * Generate cache key from namespace and key
   */
  private getCacheKey(namespace: string[], key: string): string {
    return `${namespace.join('/')}:${key}`
  }

  /**
   * Get an item from the store
   * Wraps MemoryStore.get() result in Item format for BaseStore compatibility
   */
  async get(namespace: string[], key: string): Promise<Item | null> {
    const result = await this.store.get(namespace as [string, string], key)
    if (result === null || result === undefined) {
      return null
    }

    // Get or create timestamps
    const cacheKey = this.getCacheKey(namespace, key)
    let timestamps = this.itemCache.get(cacheKey)
    if (!timestamps) {
      const now = new Date()
      timestamps = { createdAt: now, updatedAt: now }
      this.itemCache.set(cacheKey, timestamps)
    }

    return {
      value: typeof result === 'object' && result !== null
        ? (result as Record<string, unknown>)
        : { _value: result },
      key,
      namespace,
      createdAt: timestamps.createdAt,
      updatedAt: timestamps.updatedAt,
    }
  }

  /**
   * Store an item
   * Delegates to MemoryStore.put() and tracks timestamps
   */
  async put(
    namespace: string[],
    key: string,
    value: Record<string, unknown>,
    _index?: false | string[]
  ): Promise<void> {
    await this.store.put(namespace as [string, string], key, value)

    // Update timestamp cache
    const cacheKey = this.getCacheKey(namespace, key)
    const existing = this.itemCache.get(cacheKey)
    const now = new Date()
    this.itemCache.set(cacheKey, {
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    })
  }

  /**
   * Delete an item
   * Delegates to MemoryStore.delete()
   */
  async delete(namespace: string[], key: string): Promise<void> {
    await this.store.delete(namespace as [string, string], key)
    // Remove from cache
    const cacheKey = this.getCacheKey(namespace, key)
    this.itemCache.delete(cacheKey)
  }

  /**
   * Search for items in a namespace prefix
   * Returns empty array as MemoryStore.search() has different semantics
   */
  async search(
    _namespacePrefix: string[],
    _options?: {
      filter?: Record<string, unknown>
      limit?: number
      offset?: number
      query?: string
    }
  ): Promise<SearchItem[]> {
    // The iab-classifier primarily uses get/put for memory operations
    // Full search is not required for basic operation
    console.debug('[StoreAdapter] search() called - returning empty (not required for classifier)')
    return []
  }

  /**
   * List namespaces - simplified implementation
   */
  async listNamespaces(_options?: {
    prefix?: string[]
    suffix?: string[]
    maxDepth?: number
    limit?: number
    offset?: number
  }): Promise<string[][]> {
    // Not required for classifier operation
    console.debug('[StoreAdapter] listNamespaces() called - returning empty')
    return []
  }

  /**
   * Batch operations
   */
  async batch<Op extends Operation[]>(operations: Op): Promise<OperationResults<Op>> {
    const results: unknown[] = []

    for (const op of operations) {
      if ('key' in op && 'namespace' in op && 'value' in op) {
        // PutOperation
        if (op.value !== null) {
          await this.put(op.namespace, op.key, op.value as Record<string, unknown>)
        }
        results.push(undefined)
      } else if ('key' in op && 'namespace' in op) {
        // GetOperation
        const item = await this.get(op.namespace, op.key)
        results.push(item)
      } else if ('namespacePrefix' in op) {
        // SearchOperation
        const items = await this.search(op.namespacePrefix, {
          filter: op.filter,
          limit: op.limit,
          offset: op.offset,
          query: op.query,
        })
        results.push(items)
      } else if ('matchConditions' in op) {
        // ListNamespacesOperation
        const namespaces = await this.listNamespaces({
          limit: op.limit,
          offset: op.offset,
        })
        results.push(namespaces)
      }
    }

    return results as OperationResults<Op>
  }

  /**
   * Initialize the store (no-op for adapter)
   */
  start(): void {
    // MemoryStore doesn't need explicit initialization
  }

  /**
   * Stop the store (no-op for adapter)
   */
  stop(): void {
    // MemoryStore doesn't need explicit cleanup
    this.itemCache.clear()
  }
}

/**
 * Create a BaseStore-compatible adapter from a MemoryStore
 * Uses type assertion since MemoryStoreAdapter duck-types to BaseStore interface
 */
export function createStoreAdapter(store: MemoryStore): BaseStore {
  return new MemoryStoreAdapter(store) as unknown as BaseStore
}
